/**
 * Minimal zip reader: enough to open an .xlsx (a zip of XML files).
 *
 * Reads the central directory, then inflates single entries on demand with
 * the platform's DecompressionStream. Each deflated entry is wrapped in a
 * gzip header and trailer (CRC-32 and size come from the zip directory), so
 * it only needs the 'gzip' format, which every browser with
 * DecompressionStream supports (and Node 18, which the tests run on, lacks
 * 'deflate-raw').
 *
 * Supports stored (0) and deflate (8) entries. No zip64, no encryption:
 * spreadsheets never need either.
 */

export interface ZipEntry {
  name: string;
  method: number;
  crc32: number;
  compressedSize: number;
  uncompressedSize: number;
  localHeaderOffset: number;
}

export class ZipError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ZipError';
  }
}

const EOCD_SIG = 0x06054b50;
const CEN_SIG = 0x02014b50;
const LOC_SIG = 0x04034b50;

/** Largest file we'll inflate from one entry (guards against zip bombs). */
const MAX_ENTRY_BYTES = 200 * 1024 * 1024;

export function isZip(bytes: Uint8Array): boolean {
  return bytes.length >= 4 && bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04;
}

/** List the entries of a zip archive, keyed by path. */
export function readZipDirectory(bytes: Uint8Array): Map<string, ZipEntry> {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  // End of central directory: 22 bytes + up to 64 KiB of comment, at the end.
  let eocd = -1;
  for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 22 - 0xffff); i--) {
    if (view.getUint32(i, true) === EOCD_SIG) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new ZipError('Not a zip file');
  const count = view.getUint16(eocd + 10, true);
  let p = view.getUint32(eocd + 16, true);
  const decoder = new TextDecoder();
  const entries = new Map<string, ZipEntry>();
  for (let n = 0; n < count; n++) {
    if (p + 46 > bytes.length || view.getUint32(p, true) !== CEN_SIG) {
      throw new ZipError('Damaged zip directory');
    }
    const method = view.getUint16(p + 10, true);
    const crc32 = view.getUint32(p + 16, true);
    const compressedSize = view.getUint32(p + 20, true);
    const uncompressedSize = view.getUint32(p + 24, true);
    const nameLen = view.getUint16(p + 28, true);
    const extraLen = view.getUint16(p + 30, true);
    const commentLen = view.getUint16(p + 32, true);
    const localHeaderOffset = view.getUint32(p + 42, true);
    const name = decoder.decode(bytes.subarray(p + 46, p + 46 + nameLen));
    entries.set(name, { name, method, crc32, compressedSize, uncompressedSize, localHeaderOffset });
    p += 46 + nameLen + extraLen + commentLen;
  }
  return entries;
}

function gzipWrap(raw: Uint8Array, crc32: number, size: number): Uint8Array {
  const out = new Uint8Array(10 + raw.length + 8);
  // ID1 ID2 CM=deflate FLG=0 MTIME=0 XFL=0 OS=unknown
  out.set([0x1f, 0x8b, 8, 0, 0, 0, 0, 0, 0, 0xff], 0);
  out.set(raw, 10);
  const tail = new DataView(out.buffer, 10 + raw.length, 8);
  tail.setUint32(0, crc32 >>> 0, true);
  tail.setUint32(4, size >>> 0, true);
  return out;
}

async function inflate(data: Uint8Array): Promise<Uint8Array> {
  if (typeof DecompressionStream === 'undefined') {
    throw new ZipError("This browser can't open .xlsx files. Save the sheet as .csv and upload that instead.");
  }
  const stream = new ReadableStream<Uint8Array>({
    start(controller) {
      controller.enqueue(data);
      controller.close();
    },
  }).pipeThrough(new DecompressionStream('gzip') as unknown as ReadableWritablePair<Uint8Array, Uint8Array>);
  const reader = stream.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    total += value.length;
    if (total > MAX_ENTRY_BYTES) throw new ZipError('File is too large to open');
  }
  const out = new Uint8Array(total);
  let o = 0;
  for (const c of chunks) {
    out.set(c, o);
    o += c.length;
  }
  return out;
}

/** The uncompressed bytes of one entry. */
export async function readZipEntry(bytes: Uint8Array, entry: ZipEntry): Promise<Uint8Array> {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const p = entry.localHeaderOffset;
  if (p + 30 > bytes.length || view.getUint32(p, true) !== LOC_SIG) throw new ZipError('Damaged zip entry');
  const nameLen = view.getUint16(p + 26, true);
  const extraLen = view.getUint16(p + 28, true);
  const start = p + 30 + nameLen + extraLen;
  const raw = bytes.subarray(start, start + entry.compressedSize);
  if (entry.uncompressedSize > MAX_ENTRY_BYTES) throw new ZipError('File is too large to open');
  if (entry.method === 0) return raw;
  if (entry.method !== 8) throw new ZipError(`Unsupported zip compression (${entry.method})`);
  return inflate(gzipWrap(raw, entry.crc32, entry.uncompressedSize));
}

/** One entry as UTF-8 text, or null when the archive doesn't have it. */
export async function readZipText(
  bytes: Uint8Array,
  entries: Map<string, ZipEntry>,
  path: string,
): Promise<string | null> {
  const entry = entries.get(path) ?? entries.get(path.replace(/^\//, ''));
  if (!entry) return null;
  return new TextDecoder().decode(await readZipEntry(bytes, entry));
}
